import { useState } from "react";
import { FileText, UploadCloud, CheckCircle2 } from "lucide-react";
import { useData } from "@/context/DataContext";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Loading, Bar } from "@/pages/_shared";

const SUPPORTED = ["Bank statements", "Tax docs", "IDs", "Formation docs", "Invoices", "Contracts", "Financial statements", "Pay stubs", "Underwriting docs"];
const FILTERS = ["All", "Verified", "Pending", "Processing"];

export default function Documents() {
  const { snapshot } = useData();
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("All");
  if (!snapshot) return <Loading />;
  const d = snapshot.documents;
  const items = filter === "All" ? d.items : d.items.filter((x) => x.state === filter.toUpperCase());
  const active = sel != null ? d.items[sel] : null;
  const activeIdx = active ? d.pipeline_stages.indexOf(active.stage) : -1;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="documents-page">
      <PageHeader eyebrow="Document Intelligence" title="Document Intelligence"
        subtitle="Upload → validate → scan → OCR → classify → extract → score → human review → attach to your Financial Graph."
        badge={<StatusChip state="BETA" />} />

      <Card className="border-dashed p-8 text-center" data-testid="document-upload-zone">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50"><UploadCloud size={26} className="text-[#1a5fd0]" /></div>
        <div className="mt-3 font-head text-[15px] font-bold text-slate-900">Drop documents to process</div>
        <p className="mt-1 text-[13px] text-slate-500">Sandbox pipeline · files are analyzed by document AI with confidence scoring and human-in-the-loop review.</p>
        <button className="mt-4 rounded-lg bg-[#1a5fd0] px-4 py-2 text-[13px] font-semibold text-white" data-testid="document-upload-btn">Select files</button>
        <div className="mt-4 flex flex-wrap justify-center gap-1.5">
          {SUPPORTED.map((s) => <span key={s} className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-500">{s}</span>)}
        </div>
      </Card>

      {active && (
        <Card className="p-5 animate-fade-up" data-testid="document-pipeline">
          <div className="mb-4 flex items-center justify-between">
            <div><div className="font-head text-[14px] font-bold text-slate-900">{active.name}</div><div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{active.type} · {Math.round(active.confidence * 100)}% confidence</div></div>
            <StatusChip state={active.state} />
          </div>
          <div className="space-y-1.5">
            {d.pipeline_stages.map((st, i) => {
              const done = i < activeIdx, current = i === activeIdx;
              return (
                <div key={st} className="flex items-center gap-3" data-testid="pipeline-stage">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${done ? "bg-emerald-500 text-white" : current ? "bg-[#1a5fd0] text-white" : "bg-slate-100 text-slate-400"}`}>
                    {done ? "✓" : i + 1}
                  </span>
                  <span className={`text-[13px] ${current ? "font-bold text-slate-900" : done ? "text-slate-500" : "text-slate-400"}`}>{st}</span>
                  {current && <span className="rounded-md bg-[#1a5fd0]/10 px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-[#1a5fd0]">Current stage</span>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <SectionTitle icon={FileText}>Documents</SectionTitle>
          <div className="flex gap-1">
            {FILTERS.map((t) => (
              <button key={t} onClick={() => { setFilter(t); setSel(null); }} data-testid={`doc-filter-${t.toLowerCase()}`}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors ${filter === t ? "bg-[#1a5fd0] text-white" : "text-slate-500 hover:bg-slate-100"}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {items.map((doc) => {
            const realIdx = d.items.indexOf(doc);
            return (
              <button key={doc.name} onClick={() => setSel(realIdx === sel ? null : realIdx)} data-testid="document-row"
                className={`flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-slate-50 ${realIdx === sel ? "bg-blue-50/40" : ""}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100"><FileText size={17} className="text-slate-500" /></div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-slate-800">{doc.name}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="w-24"><Bar pct={doc.confidence * 100} tone={doc.confidence >= 0.9 ? "#059669" : doc.confidence >= 0.7 ? "#d97706" : "#dc2626"} height={5} /></div>
                    <span className="font-mono text-[10px] text-slate-400">{Math.round(doc.confidence * 100)}% · {doc.stage}</span>
                  </div>
                </div>
                <StatusChip state={doc.state} />
              </button>
            );
          })}
          {items.length === 0 && <div className="px-5 py-8 text-center text-[13px] text-slate-400">No documents with status {filter}.</div>}
        </div>
      </Card>
    </div>
  );
}
